import axios from 'axios';

import { createClientOperationId, EventJobStartResponseDTO } from '@/api/events/api';
import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import { createLogger } from '@/utils/logging';

const logger = createLogger('KnowledgeAPI', 'api');

/** Typed error thrown by all knowledge API functions. */
export class KnowledgeApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'KnowledgeApiError';
  }
}

/** Request body for creating a markdown note knowledge card. */
export interface CreateKnowledgeNoteRequestDTO {
  project_id?: string | null;
  /** When set (UUID), persisted as `knowledge_cards.id` — use asset session / draft id. */
  card_id?: string | null;
  title: string;
  markdown_content: string;
  summary_text?: string | null;
  tags: string[];
}

/** Request body for replacing the content of an existing note card. */
export interface UpdateKnowledgeNoteRequestDTO {
  project_id?: string | null;
  title: string;
  markdown_content: string;
  summary_text?: string | null;
  tags: string[];
}

/** Request body for updating the title and editable summary of an uploaded document card. */
export interface UpdateKnowledgeDocumentSummaryRequestDTO {
  project_id?: string | null;
  title: string;
  /** Markdown summary (LLM + user edits). */
  summary_text: string;
}

/** A knowledge card record as returned by the API (shared across note and document types). */
export interface KnowledgeCardResponseDTO {
  card_id: string;
  client_id: string;
  project_id?: string | null;
  source_type: 'note' | 'document' | 'file_url' | 'markdown_import';
  title: string;
  source_url?: string | null;
  summary_text?: string | null;
  ingestion_status: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

/** Payload when ``found`` is true on ``GET .../knowledge/cards/{card_id}`` (note editor). */
export interface KnowledgeCardNotePayloadDTO {
  card_id: string;
  client_id: string;
  project_id?: string | null;
  title: string;
  tags: string[];
  markdown_content: string;
  source_type?: 'note' | 'document' | 'file_url' | 'markdown_import';
  source_url?: string | null;
  summary_text?: string | null;
  ingestion_status?: string;
}

/** Response from the single-card load endpoint; `found` is false when the card doesn't exist yet. */
export interface KnowledgeCardLoadResponseDTO {
  found: boolean;
  card?: KnowledgeCardNotePayloadDTO | null;
}

/** Response from a document ingestion job enqueue call (legacy synchronous or async path). */
export interface KnowledgeIngestionJobResponseDTO {
  job_id: string;
  status: 'queued' | 'running' | 'failed' | 'completed';
  card_id?: string | null;
  error_message?: string | null;
  event_job_id?: string | null;
  stream_url?: string | null;
  job_stream_url?: string | null;
  status_url?: string | null;
  cancel_url?: string | null;
  latest_event_id?: number | null;
  latest_global_event_id?: number | null;
}

/** Request body for ingesting a document from a remote file URL (e.g., SharePoint). */
export interface RegisterKnowledgeFileUrlRequestDTO {
  project_id?: string | null;
  source_url: string;
  title: string;
  file_type: 'docx' | 'pptx' | 'xlsx' | 'csv' | 'pdf';
  tags?: string[];
  summary_text?: string | null;
  client_operation_id?: string | null;
}

const knowledgeApiClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: 10000,
});

/**
 * Normalises any caught value into a KnowledgeApiError, extracting the backend `detail` message
 * where available.
 */
const parseKnowledgeApiError = (error: unknown): KnowledgeApiError => {
  handleSessionExpired(error);

  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : 'Request failed';
    return new KnowledgeApiError(message);
  }

  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return new KnowledgeApiError(detail);
  }
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    const nested = d.detail;
    if (typeof nested === 'string' && nested.trim().length > 0) {
      return new KnowledgeApiError(nested);
    }
    const msg = d.message;
    if (typeof msg === 'string' && msg.trim().length > 0) {
      return new KnowledgeApiError(msg);
    }
  }

  return new KnowledgeApiError(error.message);
};

/** Controls how verbose the LLM-generated document summary will be. */
export type WordDocumentSummaryMode = 'concise' | 'detailed';

type UploadProjectDocumentToKnowledgeOptions = {
  /**
   * Portal access token for the Blend backend API.
   *
   * Note: this is not a Microsoft token. Microsoft OBO assertion is passed separately.
   */
  accessToken: string;
  clientId: string;
  file: File;
  summaryMode: WordDocumentSummaryMode;
  endpointPath: string;
  operationLabel: string;
  /**
   * Optional Entra access token used for Graph OBO exchange.
   *
   * When provided, we send it both as:
   * - `X-Microsoft-OBO-Assertion` header
   * - `microsoft_obo_assertion` multipart form field
   *
   * The backend may fall back to app-only Graph permissions if OBO fails/expired.
   */
  microsoftOboAssertion?: string | null;
  /** Optional UUID to align the created card id with the asset-session draft id. */
  assetSessionId?: string | null;
};

type StartProjectDocumentJobOptions = UploadProjectDocumentToKnowledgeOptions & {
  clientOperationId?: string;
};

/**
 * Shared multipart upload helper for all project document types (Word, PowerPoint, Tabular, PDF).
 * Synchronous — blocks until the backend has ingested and indexed the document.
 */
const uploadProjectDocumentToKnowledge = async (
  options: UploadProjectDocumentToKnowledgeOptions,
): Promise<KnowledgeCardResponseDTO> => {
  const {
    accessToken,
    file,
    summaryMode,
    endpointPath,
    operationLabel,
    microsoftOboAssertion,
    assetSessionId,
  } = options;
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('summary_mode', summaryMode);
    if (assetSessionId && assetSessionId.trim().length > 0) {
      form.append('asset_session_id', assetSessionId.trim());
    }
    const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
    const obo = microsoftOboAssertion?.trim();
    if (obo) {
      headers['X-Microsoft-OBO-Assertion'] = obo;
      form.append('microsoft_obo_assertion', obo);
    }
    const response = await knowledgeApiClient.post<KnowledgeCardResponseDTO>(endpointPath, form, {
      headers,
      timeout: 120000,
    });
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error(`Failed to upload ${operationLabel}`, parsed);
    throw parsed;
  }
};

/**
 * Enqueues ingestion of a remote file URL (e.g., a SharePoint document link) into the knowledge base.
 * POST /clients/{clientId}/knowledge/file-urls
 *
 * @returns Job status including the card ID once ingestion completes.
 */
export const registerKnowledgeFileUrlJob = async (
  accessToken: string,
  clientId: string,
  payload: RegisterKnowledgeFileUrlRequestDTO,
): Promise<KnowledgeIngestionJobResponseDTO> => {
  try {
    const response = await knowledgeApiClient.post<KnowledgeIngestionJobResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/file-urls`,
      {
        ...payload,
        client_operation_id: payload.client_operation_id ?? createClientOperationId('file-url'),
      },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 30000,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to start file URL ingestion job', parsed);
    throw parsed;
  }
};

/**
 * Enqueues a generic document upload job for the client's knowledge base (not project-scoped).
 * POST /clients/{clientId}/knowledge/documents
 *
 * @param fileType - Extension hint used by the backend parser to select the correct extractor.
 * @returns Job status record that can be polled for completion.
 */
export const startGenericKnowledgeDocumentJob = async (
  accessToken: string,
  clientId: string,
  file: File,
  fileType: RegisterKnowledgeFileUrlRequestDTO['file_type'],
  options?: {
    projectId?: string | null;
    title?: string | null;
    tags?: string[];
    summaryText?: string | null;
    clientOperationId?: string | null;
  },
): Promise<KnowledgeIngestionJobResponseDTO> => {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('file_type', fileType);
    form.append(
      'client_operation_id',
      options?.clientOperationId ?? createClientOperationId('knowledge-document'),
    );
    if (options?.projectId) {
      form.append('project_id', options.projectId);
    }
    if (options?.title) {
      form.append('title', options.title);
    }
    options?.tags?.forEach((tag) => form.append('tags', tag));
    if (options?.summaryText) {
      form.append('summary_text', options.summaryText);
    }
    const response = await knowledgeApiClient.post<KnowledgeIngestionJobResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/documents`,
      form,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 30000,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to start generic document ingestion job', parsed);
    throw parsed;
  }
};

/**
 * Shared async job enqueue helper for all project document upload variants.
 * Returns the job handle for SSE streaming rather than blocking on completion.
 */
const startProjectDocumentJob = async (
  options: StartProjectDocumentJobOptions,
): Promise<EventJobStartResponseDTO> => {
  const {
    accessToken,
    file,
    summaryMode,
    endpointPath,
    operationLabel,
    microsoftOboAssertion,
    assetSessionId,
    clientOperationId,
  } = options;
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('summary_mode', summaryMode);
    form.append('client_operation_id', clientOperationId ?? createClientOperationId('upload'));
    if (assetSessionId && assetSessionId.trim().length > 0) {
      form.append('asset_session_id', assetSessionId.trim());
    }
    const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
    const obo = microsoftOboAssertion?.trim();
    if (obo) {
      headers['X-Microsoft-OBO-Assertion'] = obo;
      form.append('microsoft_obo_assertion', obo);
    }
    const response = await knowledgeApiClient.post<EventJobStartResponseDTO>(endpointPath, form, {
      headers,
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error(`Failed to start ${operationLabel} job`, parsed);
    throw parsed;
  }
};

/** Multipart upload of a .docx to project SharePoint + knowledge card (dual-index). */
export const uploadProjectWordDocument = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<KnowledgeCardResponseDTO> => {
  return uploadProjectDocumentToKnowledge({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/word-documents`,
    operationLabel: 'Word document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/**
 * Enqueues an async .docx upload job for a project, returning the SSE job handle.
 * POST /clients/{clientId}/projects/{projectId}/knowledge/word-document-jobs
 *
 * @returns Job start metadata for SSE streaming.
 */
export const startProjectWordDocumentJob = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<EventJobStartResponseDTO> => {
  return startProjectDocumentJob({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/word-document-jobs`,
    operationLabel: 'Word document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/** Multipart upload of a .pptx to project SharePoint + knowledge card (dual-index). */
export const uploadProjectPowerPointDocument = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<KnowledgeCardResponseDTO> => {
  return uploadProjectDocumentToKnowledge({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/powerpoint-documents`,
    operationLabel: 'PowerPoint document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/**
 * Enqueues an async .pptx upload job for a project, returning the SSE job handle.
 * POST /clients/{clientId}/projects/{projectId}/knowledge/powerpoint-document-jobs
 *
 * @returns Job start metadata for SSE streaming.
 */
export const startProjectPowerPointDocumentJob = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<EventJobStartResponseDTO> => {
  return startProjectDocumentJob({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/powerpoint-document-jobs`,
    operationLabel: 'PowerPoint document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/** Multipart upload of a .xlsx or .csv to project SharePoint + knowledge card (dual-index). */
export const uploadProjectTabularDocument = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<KnowledgeCardResponseDTO> => {
  return uploadProjectDocumentToKnowledge({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/tabular-documents`,
    operationLabel: 'Excel/CSV document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/**
 * Enqueues an async .xlsx/.csv upload job for a project, returning the SSE job handle.
 * POST /clients/{clientId}/projects/{projectId}/knowledge/tabular-document-jobs
 *
 * @returns Job start metadata for SSE streaming.
 */
export const startProjectTabularDocumentJob = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<EventJobStartResponseDTO> => {
  return startProjectDocumentJob({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/tabular-document-jobs`,
    operationLabel: 'Excel/CSV document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/** Multipart upload of a .pdf to project SharePoint + knowledge card (dual-index). */
export const uploadProjectPdfDocument = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<KnowledgeCardResponseDTO> => {
  return uploadProjectDocumentToKnowledge({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/pdf-documents`,
    operationLabel: 'PDF document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/**
 * Enqueues an async .pdf upload job for a project, returning the SSE job handle.
 * POST /clients/{clientId}/projects/{projectId}/knowledge/pdf-document-jobs
 *
 * @returns Job start metadata for SSE streaming.
 */
export const startProjectPdfDocumentJob = async (
  accessToken: string,
  clientId: string,
  projectId: string,
  file: File,
  summaryMode: WordDocumentSummaryMode,
  microsoftOboAssertion?: string | null,
  assetSessionId?: string | null,
): Promise<EventJobStartResponseDTO> => {
  return startProjectDocumentJob({
    accessToken,
    clientId,
    file,
    summaryMode,
    endpointPath: `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/knowledge/pdf-document-jobs`,
    operationLabel: 'PDF document',
    microsoftOboAssertion,
    assetSessionId,
  });
};

/**
 * Creates a markdown note knowledge card for the given client.
 * POST /clients/{clientId}/knowledge/notes
 *
 * @returns The newly created knowledge card record.
 */
export const createKnowledgeNote = async (
  accessToken: string,
  clientId: string,
  payload: CreateKnowledgeNoteRequestDTO,
): Promise<KnowledgeCardResponseDTO> => {
  try {
    const response = await knowledgeApiClient.post<KnowledgeCardResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/notes`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to create knowledge note', parsed);
    throw parsed;
  }
};

/**
 * Replaces the content of an existing note card.
 * PUT /clients/{clientId}/knowledge/notes/{cardId}
 *
 * @returns The updated knowledge card record.
 */
export const updateKnowledgeNote = async (
  accessToken: string,
  clientId: string,
  cardId: string,
  payload: UpdateKnowledgeNoteRequestDTO,
): Promise<KnowledgeCardResponseDTO> => {
  try {
    const response = await knowledgeApiClient.put<KnowledgeCardResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/notes/${encodeURIComponent(cardId)}`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to update knowledge note', parsed);
    throw parsed;
  }
};

/**
 * Updates the human-editable summary text and title of an uploaded document card.
 * PUT /clients/{clientId}/knowledge/documents/{cardId}/summary
 *
 * @returns The updated knowledge card record.
 */
export const updateKnowledgeDocumentSummary = async (
  accessToken: string,
  clientId: string,
  cardId: string,
  payload: UpdateKnowledgeDocumentSummaryRequestDTO,
): Promise<KnowledgeCardResponseDTO> => {
  try {
    const response = await knowledgeApiClient.put<KnowledgeCardResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/documents/${encodeURIComponent(cardId)}/summary`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to update document summary', parsed);
    throw parsed;
  }
};

/* ------------------------------------------------------------------ */
/*  Tag Management — Transport Only                                    */
/* ------------------------------------------------------------------ */

/** A tag definition as returned by the tags API. */
export interface KnowledgeTagResponseDTO {
  tag_id: string;
  tag_name: string;
  tag_key: string;
  description?: string | null;
  usage_count: number;
  created_at: string;
  client_id?: string;
  project_id?: string | null;
}

/** Paginated list of tag definitions. */
export interface KnowledgeTagListResponseDTO {
  items: KnowledgeTagResponseDTO[];
  total: number;
}

/** Request body for creating a new tag definition. */
export interface CreateKnowledgeTagRequestDTO {
  project_id?: string | null;
  tag_name: string;
  description?: string | null;
}

/** Request body for bulk-assigning tag names to a card. */
export interface AssignKnowledgeCardTagsRequestDTO {
  tags: string[];
}

/** Fetch all tags for a client, optionally scoped to a project. */
export const listKnowledgeTags = async (
  accessToken: string,
  clientId: string,
  projectId?: string | null,
): Promise<KnowledgeTagListResponseDTO> => {
  try {
    const response = await knowledgeApiClient.get<KnowledgeTagListResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/tags`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: projectId ? { project_id: projectId } : undefined,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to list knowledge tags', parsed);
    throw parsed;
  }
};

/** Create a new tag definition. */
export const createKnowledgeTag = async (
  accessToken: string,
  clientId: string,
  payload: CreateKnowledgeTagRequestDTO,
): Promise<KnowledgeTagResponseDTO> => {
  try {
    const response = await knowledgeApiClient.post<KnowledgeTagResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/tags`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to create knowledge tag', parsed);
    throw parsed;
  }
};

/** Assign one or more tags to a card by name. */
export const assignKnowledgeCardTags = async (
  accessToken: string,
  clientId: string,
  cardId: string,
  payload: AssignKnowledgeCardTagsRequestDTO,
): Promise<KnowledgeCardResponseDTO> => {
  try {
    const response = await knowledgeApiClient.post<KnowledgeCardResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/cards/${encodeURIComponent(cardId)}/tags`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to assign knowledge card tags', parsed);
    throw parsed;
  }
};

/** Remove a single tag from a card by tag id. */
export const removeKnowledgeCardTag = async (
  accessToken: string,
  clientId: string,
  cardId: string,
  tagId: string,
): Promise<KnowledgeCardResponseDTO> => {
  try {
    const response = await knowledgeApiClient.delete<KnowledgeCardResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/cards/${encodeURIComponent(cardId)}/tags/${encodeURIComponent(tagId)}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to remove knowledge card tag', parsed);
    throw parsed;
  }
};

/** Delete a knowledge card (cascades chunks/edges/tag assignments server-side). */
export const deleteKnowledgeCard = async (
  accessToken: string,
  clientId: string,
  cardId: string,
): Promise<void> => {
  try {
    await knowledgeApiClient.delete(
      `/clients/${encodeURIComponent(clientId)}/knowledge/cards/${encodeURIComponent(cardId)}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to delete knowledge card', parsed);
    throw parsed;
  }
};

/* ------------------------------------------------------------------ */
/*  Card Listing — Transport Only                                      */
/* ------------------------------------------------------------------ */

/** Paginated list response for knowledge card queries. */
export interface KnowledgeCardListResponseDTO {
  items: KnowledgeCardResponseDTO[];
  total: number;
}

/** Query parameters for filtering and paginating the knowledge card list endpoint. */
export interface ListKnowledgeCardsParams {
  project_id?: string | null;
  source_type?: string | null;
  tag?: string | null;
  q?: string | null;
  page?: number;
  page_size?: number;
}

/** Fetch paginated list of knowledge cards for a client. */
export const listKnowledgeCards = async (
  accessToken: string,
  clientId: string,
  params?: ListKnowledgeCardsParams,
): Promise<KnowledgeCardListResponseDTO> => {
  try {
    /* Strip null/undefined params so axios doesn't send empty query strings. */
    const cleanParams: Record<string, string | number> = {};
    if (params) {
      if (params.project_id) {
        cleanParams.project_id = params.project_id;
      }
      if (params.source_type) {
        cleanParams.source_type = params.source_type;
      }
      if (params.tag) {
        cleanParams.tag = params.tag;
      }
      if (params.q) {
        cleanParams.q = params.q;
      }
      if (params.page !== undefined) {
        cleanParams.page = params.page;
      }
      if (params.page_size !== undefined) {
        cleanParams.page_size = params.page_size;
      }
    }
    const response = await knowledgeApiClient.get<KnowledgeCardListResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/cards`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to list knowledge cards', parsed);
    throw parsed;
  }
};

/* ------------------------------------------------------------------ */
/*  Note Card — Load                                                   */
/* ------------------------------------------------------------------ */

/**
 * Loads a single knowledge card by ID for the note editor.
 * GET /clients/{clientId}/knowledge/cards/{cardId}
 *
 * @returns `{ found: false }` when the card does not exist yet (new asset session).
 */
export const fetchKnowledgeNoteCard = async (
  accessToken: string,
  clientId: string,
  cardId: string,
  projectId?: string | null,
): Promise<KnowledgeCardLoadResponseDTO> => {
  try {
    const response = await knowledgeApiClient.get<KnowledgeCardLoadResponseDTO>(
      `/clients/${encodeURIComponent(clientId)}/knowledge/cards/${encodeURIComponent(cardId)}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: projectId ? { project_id: projectId } : undefined,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseKnowledgeApiError(error);
    logger.error('Failed to load knowledge card', parsed);
    throw parsed;
  }
};
